import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Crown, Users, UserCheck, DollarSign, Loader2, Check, Sparkles, ArrowUp, Clock, CheckCircle } from 'lucide-react';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { toast } from 'sonner';

interface SubscriptionPlan {
  id: string;
  plan_key: string;
  name: string;
  price: number;
  patient_limit: number | null;
  supervisor_limit: number | null;
  features: string[];
  is_active: boolean;
  sort_order: number;
}

interface PendingRequest {
  id: string;
  requested_plan: string;
  status: string;
  created_at: string;
}

export function PlanViewer() {
  const { user } = useAdminAuth();
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPlanKey, setCurrentPlanKey] = useState<string | null>(null);
  const [pendingRequest, setPendingRequest] = useState<PendingRequest | null>(null);
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);
  const [requestMessage, setRequestMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadPlans();
    loadCurrentPlan();
    loadPendingRequest();
  }, [user?.agency_id]);

  const loadCurrentPlan = async () => {
    if (!user?.agency_id) return;
    
    try {
      const { data } = await supabase
        .from('agencies')
        .select('plan')
        .eq('id', user.agency_id)
        .single();
      
      if (data) {
        setCurrentPlanKey(data.plan);
      }
    } catch (error) {
      console.error('Error loading current plan:', error);
    }
  };

  const loadPendingRequest = async () => {
    if (!user?.agency_id) return;
    
    try {
      const { data } = await supabase
        .from('plan_upgrade_requests')
        .select('id, requested_plan, status, created_at')
        .eq('agency_id', user.agency_id)
        .eq('status', 'pending')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      
      if (data) {
        setPendingRequest(data);
      }
    } catch (error) {
      console.error('Error loading pending request:', error);
    }
  };

  const loadPlans = async () => {
    try {
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('*')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });

      if (error) throw error;
      
      const mappedPlans: SubscriptionPlan[] = (data || []).map(p => ({
        id: p.id,
        plan_key: p.plan_key,
        name: p.name,
        price: p.price,
        patient_limit: p.patient_limit,
        supervisor_limit: p.supervisor_limit,
        features: Array.isArray(p.features) ? (p.features as string[]) : [],
        is_active: p.is_active,
        sort_order: p.sort_order,
      }));
      setPlans(mappedPlans);
    } catch (error) {
      console.error('Error loading plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const openRequestDialog = (plan: SubscriptionPlan) => {
    setSelectedPlan(plan);
    setRequestMessage('');
    setRequestDialogOpen(true);
  };

  const handleSubmitRequest = async () => {
    if (!selectedPlan || !user?.agency_id || !currentPlanKey) return;
    
    setSubmitting(true);
    try {
      const { error } = await supabase
        .from('plan_upgrade_requests')
        .insert({
          agency_id: user.agency_id,
          requested_by: user.id,
          current_plan: currentPlanKey,
          requested_plan: selectedPlan.plan_key,
          message: requestMessage || null,
          status: 'pending',
        });

      if (error) throw error;
      
      toast.success('Plan upgrade request submitted successfully!');
      setRequestDialogOpen(false);
      loadPendingRequest();
    } catch (error: any) {
      console.error('Error submitting request:', error);
      toast.error(error.message || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Card className="shadow-card">
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Pending Request Banner */}
      {pendingRequest && (
        <Card className="border-amber-200 bg-amber-50/50 dark:border-amber-900 dark:bg-amber-950/20">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-amber-100 dark:bg-amber-900">
                <Clock className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-amber-800 dark:text-amber-200">
                  Pending Plan Upgrade Request
                </p>
                <p className="text-sm text-amber-700 dark:text-amber-300">
                  You have requested to upgrade to the <span className="font-semibold capitalize">{pendingRequest.requested_plan.replace('_', ' ')}</span> plan. 
                  Our team will review your request shortly.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="shadow-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Crown className="h-5 w-5 text-primary" />
            Available Subscription Plans
          </CardTitle>
          <CardDescription>
            View available subscription plans and their features. Click on a plan to request an upgrade.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {currentPlanKey && (
            <div className="mb-6 p-4 rounded-lg bg-primary/5 border border-primary/20">
              <p className="text-sm text-muted-foreground">
                Your current plan: <span className="font-semibold text-primary capitalize">{currentPlanKey.replace('_', ' ')}</span>
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {plans.map((plan) => {
              const isCurrentPlan = plan.plan_key === currentPlanKey;
              const isPendingPlan = pendingRequest?.requested_plan === plan.plan_key;
              
              return (
                <Card 
                  key={plan.id} 
                  className={`relative overflow-hidden transition-all ${
                    isCurrentPlan 
                      ? 'border-primary shadow-lg ring-2 ring-primary/20' 
                      : isPendingPlan
                        ? 'border-amber-400 ring-2 ring-amber-200'
                        : 'hover:shadow-md'
                  }`}
                >
                  {isCurrentPlan && (
                    <div className="absolute top-0 right-0">
                      <Badge className="rounded-none rounded-bl-lg bg-primary text-primary-foreground">
                        <Sparkles className="h-3 w-3 mr-1" />
                        Current
                      </Badge>
                    </div>
                  )}
                  {isPendingPlan && !isCurrentPlan && (
                    <div className="absolute top-0 right-0">
                      <Badge className="rounded-none rounded-bl-lg bg-amber-500 text-white">
                        <Clock className="h-3 w-3 mr-1" />
                        Pending
                      </Badge>
                    </div>
                  )}
                  
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">{plan.name}</CardTitle>
                    <div className="flex items-baseline gap-1">
                      <DollarSign className="h-4 w-4 text-muted-foreground" />
                      <span className="text-3xl font-bold">{plan.price}</span>
                      <span className="text-sm text-muted-foreground">/month</span>
                    </div>
                  </CardHeader>
                  
                  <CardContent className="space-y-4">
                    {/* Limits */}
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm">
                        <Users className="h-4 w-4 text-muted-foreground" />
                        <span>
                          {plan.patient_limit !== null 
                            ? `Up to ${plan.patient_limit.toLocaleString()} patients` 
                            : 'Unlimited patients'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <UserCheck className="h-4 w-4 text-muted-foreground" />
                        <span>
                          {plan.supervisor_limit !== null 
                            ? `Up to ${plan.supervisor_limit} supervisors` 
                            : 'Unlimited supervisors'}
                        </span>
                      </div>
                    </div>

                    {/* Features */}
                    {plan.features.length > 0 && (
                      <div className="space-y-2 pt-2 border-t border-border">
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                          Features
                        </p>
                        <ul className="space-y-1.5">
                          {plan.features.slice(0, 5).map((feature, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-sm">
                              <Check className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                              <span>{feature}</span>
                            </li>
                          ))}
                          {plan.features.length > 5 && (
                            <li className="text-sm text-muted-foreground">
                              +{plan.features.length - 5} more features
                            </li>
                          )}
                        </ul>
                      </div>
                    )}

                    {/* Request Upgrade Button */}
                    {!isCurrentPlan && !pendingRequest && (
                      <Button 
                        className="w-full mt-4" 
                        variant="outline"
                        onClick={() => openRequestDialog(plan)}
                      >
                        <ArrowUp className="h-4 w-4 mr-2" />
                        Request This Plan
                      </Button>
                    )}
                    {isCurrentPlan && (
                      <div className="mt-4 text-center">
                        <Badge variant="secondary" className="gap-1">
                          <CheckCircle className="h-3 w-3" />
                          Active Plan
                        </Badge>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {plans.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              No subscription plans available.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Request Dialog */}
      <Dialog open={requestDialogOpen} onOpenChange={setRequestDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request Plan Upgrade</DialogTitle>
            <DialogDescription>
              Submit a request to upgrade to the <span className="font-semibold capitalize">{selectedPlan?.name}</span> plan. 
              Our team will review your request and get back to you.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-4 rounded-lg bg-muted/50 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Current Plan:</span>
                <span className="font-medium capitalize">{currentPlanKey?.replace('_', ' ')}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Requested Plan:</span>
                <span className="font-medium text-primary">{selectedPlan?.name}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">New Price:</span>
                <span className="font-medium">${selectedPlan?.price}/month</span>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="message">Additional Notes (Optional)</Label>
              <Textarea
                id="message"
                placeholder="Add any additional information about your upgrade request..."
                value={requestMessage}
                onChange={(e) => setRequestMessage(e.target.value)}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setRequestDialogOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button onClick={handleSubmitRequest} disabled={submitting}>
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Request'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
